/**
 * Pre-run expense-coverage callout (INS-12686), rendered in Eval Settings under
 * the run's scenario rows.
 *
 * An expense area a property matches is a COMPLETE expense picture: the calculator
 * charges $0 for any of the four zeroable cost types (LOE schedule, oil/gas/water
 * operating expense) it finds nothing in the area to charge for, rather than
 * inheriting the legacy Scenario's cost.
 * Sandbox package 28089 had two Delaware tiers with no expense lines at all, so
 * every property in them was silently charged $0 four ways and nothing said so.
 * This says so — BEFORE the eval runs, which is the only point at which the user
 * can still fix it.
 *
 * The finding is scenario-INTRINSIC: it counts what the scenario's areas author,
 * with no slot resolution and no package in play. That is what makes a pre-run
 * warning possible at all. A run prices with ONE expense scenario, case-wide —
 * the Expenses row of `run_scenarios` (the run's override, else the case's) — so
 * that is the one scenario checked here.
 *
 * The dangerous failure for this feature is a silent false "no gap": blank space
 * reads as "checked, clean", so a scenario this component cannot examine must
 * SAY it cannot examine it rather than fall through to `null`. That happens when
 * the run's scenario is MISSING from the loaded list — `useDomainScenarios` asks
 * for `pageSize: 500` sorted `updated_at DESC`, so a very stale, deleted, or
 * counts-less row resolves to an id nothing here can answer for.
 *
 * `null` is still correct for the genuine non-events: the rows or the list are
 * still loading, the run has no expense scenario, or it is fully covered. A
 * "couldn't check" flash on every page load while the list resolves would be
 * noise, so the unchecked notice is gated on the list having arrived.
 *
 * A FAILED list request is not one of those non-events: it leaves the list
 * `undefined` exactly as loading does, and rendering `null` for it would show
 * "clean" for a check that never ran. It gets its own error notice with a Retry,
 * the same affordance the scenario rows above use.
 */

import { useId } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { PlainLink } from 'components/link';
import { scenarioHref } from 'lib/evaluation-case';
import type { DomainScenarioOption, RunScenarioRow } from '../types';
import {
  NAMED_AREA_LIMIT,
  asExpenseScenarioOptions,
  moreAreasLine,
  uncheckedScenarioSubtitle,
  uncheckedScenarioTitle,
  uncoveredAreaLine,
  uncoveredAreasSubtitle,
  uncoveredAreasTitle,
} from './utils';
import styles from './expense-coverage-notification.module.scss';

interface ExpenseCoverageNotificationProps {
  /**
   * The run's Expenses row from `run_scenarios` — the scenario this run would
   * price with, override included. `undefined` while the rows load, or while
   * they still answer for the previous overrides.
   */
  expenseRow: RunScenarioRow | undefined;
  /** The expenses scenario list (`useDomainScenarios('expenses')`), possibly still loading. */
  expenseScenarios: DomainScenarioOption[] | undefined;
  /** Whether that list's request failed — NOT the same as still loading. */
  expenseScenariosError: boolean;
  /** Re-requests the list (the query's `refetch`). */
  onRetry: () => void;
}

/**
 * The run's expense-coverage notices: a gap warning, an unchecked notice, and a failed-list error.
 *
 * @param {ExpenseCoverageNotificationProps} props - The run's Expenses row and the expense list.
 * @return {JSX.Element | null} The notices, or null when there is nothing to say.
 */
export function ExpenseCoverageNotification({
  expenseRow,
  expenseScenarios,
  expenseScenariosError,
  onRetry,
}: ExpenseCoverageNotificationProps) {
  // The label the link row is named by, so "Review:" / "Check:" is read as that
  // row's heading rather than as a stray word before the link.
  const linkLabelId = useId();

  // No expense scenario on this run (or no rows yet): nothing to price with, so
  // nothing to check. Readiness already names the missing domain on its row.
  if (expenseRow === undefined) {
    return null;
  }
  // run_scenarios names every scenario it sends (a name is required), so a row
  // with an id always has one.
  const { scenarioId, scenarioName: name } = expenseRow;
  if (scenarioId === null || name === null) {
    return null;
  }

  const listLoaded = expenseScenarios !== undefined;
  const option = asExpenseScenarioOptions(expenseScenarios).find(
    (candidate) => candidate.id === scenarioId
  );
  const gapped = option !== undefined && option.uncoveredAreas.length > 0;
  // An id with no row carries no answer about whether it has a gap. It must not
  // be assumed covered OR assumed gapped — it is said out loud. Not while the
  // list is loading, when every id is unresolvable.
  const unchecked = listLoaded && option === undefined;

  // Nothing gapped, nothing unexaminable, nothing failed — the genuine non-event.
  if (!gapped && !unchecked && !expenseScenariosError) {
    return null;
  }

  // The run's row is the authority on what the run uses, so its name (above) is
  // the one shown everywhere here — never the list row's, which can lag a rename.
  const href = scenarioHref(expenseRow);
  const moreAreas = option ? moreAreasLine(option.uncoveredAreas.length) : null;

  return (
    <section className={styles.container} aria-label="Expense coverage">
      {/* Two failures, two sentences. React Query keeps the last good list when
          a REFETCH fails (a focus re-read after a network blip), and the notices
          below are still built from it — so "cannot be reported" would contradict
          the warning it sits beside. That case says "may be out of date". */}
      {expenseScenariosError && (
        <ActionableNotification
          className={styles.notification}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title={
            listLoaded
              ? "Couldn't refresh expense coverage"
              : "Couldn't check expense coverage"
          }
          subtitle={
            listLoaded
              ? 'The Expense Scenario list could not be re-read, so the coverage shown here may be out of date.'
              : 'The Expense Scenario list is unavailable right now, so areas that may charge $0 cannot be reported.'
          }
          actionButtonLabel="Retry"
          onActionButtonClick={onRetry}
        />
      )}
      {(gapped || unchecked) && (
        <>
          {gapped ? (
            // Names each area and the cost types it is charged $0 for, so the fix needs no
            // area-by-area search of the scenario. Plain text: the notification
            // allows no interactive children (see the link row below).
            <InlineNotification
              className={styles.notification}
              kind="warning"
              lowContrast
              hideCloseButton
              title={uncoveredAreasTitle(option.uncoveredAreas.length)}
              subtitle={uncoveredAreasSubtitle(name, option)}
            >
              <ul className={styles.areaList} aria-label="Uncovered areas">
                {option.uncoveredAreas
                  .slice(0, NAMED_AREA_LIMIT)
                  .map((area) => (
                    <li key={area.id}>{uncoveredAreaLine(area)}</li>
                  ))}
                {moreAreas && <li>{moreAreas}</li>}
              </ul>
            </InlineNotification>
          ) : (
            // `info`, NOT `warning`: nothing here is a known problem. A warning
            // would claim the scenario IS broken, which is the mirror image of the
            // silence it replaces. Re-check re-reads the list, so a scenario created
            // in another tab is found without waiting for a focus re-read.
            <ActionableNotification
              className={styles.notification}
              inline
              kind="info"
              // A status, not Carbon's default alertdialog: it announces politely and traps no focus.
              role="status"
              hasFocus={false}
              lowContrast
              hideCloseButton
              title={uncheckedScenarioTitle(name)}
              subtitle={uncheckedScenarioSubtitle()}
              actionButtonLabel="Re-check"
              onActionButtonClick={onRetry}
            />
          )}
          {/* The route to the fix, as a SIBLING of the notification rather than
              inside its subtitle: Carbon's InlineNotification asserts it has no
              interactive descendants (`useNoInteractiveChildren` throws outside
              production on any focusable child), and its `subtitle` prop is
              typed `string`, so a link in there would hard-fail the page in dev
              and in tests. */}
          <div
            className={styles.links}
            role="group"
            aria-labelledby={linkLabelId}
          >
            <span id={linkLabelId} className={styles.linksLabel}>
              {gapped ? 'Review:' : 'Check:'}
            </span>
            {href !== null ? (
              <PlainLink
                className={styles.scenarioLink}
                href={href}
                openInNewTab
              >
                {name}
              </PlainLink>
            ) : (
              <span>{name}</span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
